import InputAdornment from '@material-ui/core/InputAdornment';
import NoSsr from '@material-ui/core/NoSsr';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import classNames from 'classnames';
import { ReactNode } from 'react';
import {
  COLOR_TEXT_PRIMARY,
  COLOR_BORDER_INTERACTIVE,
  COLOR_BORDER_INTERACTIVE_ACTIVE,
  COLOR_BORDER_ERROR,
  COLOR_INTERACTIVE_PRIMARY,
  COLOR_TEXT_PLACEHOLDER,
  COLOR_TEXT_ERROR,
  FONT_FAMILY_PRIMARY,
  RADIUS_MD,
  RADIUS_LG,
  SPACE_4
} from '@alltrails/shared/denali/tokens';
import Key from 'types/Key';

import * as formStyles from '../../styles/styles.module.scss';
import * as styles from './styles/styles.module.scss';

const StyledTextField = withStyles({
  root: {
    display: 'flex',
    width: '100%',
    borderRadius: `${RADIUS_LG}px`,
    fontFamily: `${FONT_FAMILY_PRIMARY}, Arial, sans-serif !important`,
    '& .MuiFormLabel-root': {
      fontFamily: `${FONT_FAMILY_PRIMARY}, Arial, sans-serif !important`,
      lineHeight: '8px'
    },
    '& .MuiFormLabel-root.MuiFormLabel-filled': {
      marginTop: `${SPACE_4}px`
    },
    '& .MuiOutlinedInput-input': {
      padding: '10px 16px',
      margin: `${SPACE_4}px 0px`,
      borderRadius: `${RADIUS_MD}px`,
      color: COLOR_TEXT_PRIMARY,
      fontFamily: `${FONT_FAMILY_PRIMARY}, Arial, sans-serif !important`
    },
    '& .MuiInputLabel-outlined': {
      color: COLOR_TEXT_PLACEHOLDER,
      zIndex: 0
    },
    '& .MuiOutlinedInput-multiline': {
      padding: '0px'
    },
    '&.verticalResize .MuiOutlinedInput-input': {
      resize: 'vertical'
    },
    '& input + fieldset': {
      borderRadius: `${RADIUS_MD}px`,
      color: COLOR_TEXT_PLACEHOLDER,
      borderColor: COLOR_BORDER_INTERACTIVE
    },
    '& input:valid:focus + fieldset': {
      borderLeftWidth: '2 !important',
      borderWidth: '2',
      borderColor: `${COLOR_BORDER_INTERACTIVE_ACTIVE} !important`,
      color: COLOR_BORDER_INTERACTIVE_ACTIVE
    },
    '& input:focus + fieldset': {
      color: COLOR_INTERACTIVE_PRIMARY,
      borderColor: `${COLOR_BORDER_INTERACTIVE_ACTIVE} !important`
    },
    '&. .MuiFormHelperText-contained': {
      margin: '4px 14px'
    },
    '& .MuiFormLabel-root.Mui-error': {
      color: `${COLOR_BORDER_ERROR} !important`
    },
    '& :hover .MuiOutlinedInput-notchedOutline': {
      borderColor: `${COLOR_BORDER_INTERACTIVE_ACTIVE} !important`
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: `${RADIUS_MD}px`
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      color: `${COLOR_INTERACTIVE_PRIMARY} !important`,
      borderColor: `${COLOR_BORDER_INTERACTIVE_ACTIVE} !important`
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: COLOR_INTERACTIVE_PRIMARY,
      marginTop: `${SPACE_4}px`
    },
    '& .MuiFormLabel-root.Mui-focused.Mui-error': {
      color: COLOR_TEXT_ERROR
    },
    '& .MuiFormHelperText-root': {
      fontFamily: `${FONT_FAMILY_PRIMARY}, Arial, sans-serif !important`,
      color: `${COLOR_TEXT_ERROR} !important`
    },
    '& .MuiOutlinedInput-root.Mui-error': {
      minHeight: '47px !important' // for mobile safari
    },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
      fontFamily: `${FONT_FAMILY_PRIMARY}, Arial, sans-serif !important`,
      borderColor: `${COLOR_BORDER_ERROR} !important`,
      borderWidth: '2px',
      color: `${COLOR_TEXT_ERROR} !important`
    }
  }
})(TextField);

function SsrStyledTextField({ label }: { label: ReactNode }) {
  return <div className={styles.ssrContainer}>{label}</div>;
}

type Props = {
  adornment?: ReactNode;
  autoFocus?: boolean;
  autocomplete?: string;
  changeHandler?(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void;
  className?: string;
  enterKeyHandler?(event: React.KeyboardEvent<HTMLDivElement> | React.FormEvent): void;
  errorMessage?: ReactNode;
  inputMode?: 'numeric';
  isDisabled?: boolean;
  isRequired?: boolean;
  isSuccess?: boolean;
  label?: ReactNode;
  maxLength?: number;
  multiline?: boolean;
  name?: string;
  onBlur?(event: React.FocusEvent<HTMLInputElement>): void;
  onInput?(event: React.ChangeEvent<HTMLInputElement>): void;
  onKeyDown?(event: React.KeyboardEvent<HTMLDivElement>): void;
  onPaste?(event: React.ClipboardEvent<HTMLInputElement>): void;
  readOnly?: boolean;
  rows?: number;
  type?: string;
  value?: string;
  verticalResize?: boolean;
};

interface EndAdornmentProps {
  adornment?: ReactNode;
  isSuccess?: boolean;
}

function EndAdornment({ adornment, isSuccess }: EndAdornmentProps) {
  if (isSuccess) {
    return (
      <InputAdornment position="end">
        <CheckCircleIcon className={styles.successCheck} />
      </InputAdornment>
    );
  }

  if (adornment) {
    <InputAdornment position="end">{adornment}</InputAdornment>;
  }

  return null;
}

function OutlinedTextField({
  adornment,
  autocomplete,
  autoFocus,
  changeHandler,
  className,
  enterKeyHandler,
  errorMessage,
  inputMode,
  isDisabled,
  isRequired,
  isSuccess,
  label,
  maxLength,
  multiline,
  name,
  onBlur,
  onInput,
  onKeyDown,
  onPaste,
  readOnly,
  rows,
  type,
  value,
  verticalResize
}: Props) {
  // handle strings and FormattedMessage
  const hasError = errorMessage !== undefined && errorMessage !== '' && errorMessage !== false;

  return (
    <NoSsr fallback={<SsrStyledTextField label={label} />}>
      <StyledTextField
        autoComplete={autocomplete}
        autoFocus={autoFocus}
        className={classNames(
          className,
          styles.container,
          hasError && formStyles.inputError,
          multiline && styles.multiline,
          verticalResize && 'verticalResize'
        )}
        disabled={isDisabled}
        error={hasError}
        fullWidth
        helperText={hasError ? errorMessage : ''}
        InputProps={{
          endAdornment: <EndAdornment adornment={adornment} isSuccess={isSuccess} />,
          inputProps: {
            inputMode,
            maxLength
          },
          readOnly
        }}
        label={label}
        multiline={multiline}
        name={name}
        onBlur={onBlur}
        onChange={changeHandler}
        onInput={onInput}
        onKeyDown={onKeyDown}
        onKeyPress={e => {
          if (enterKeyHandler && e.key === Key.Enter) {
            enterKeyHandler(e);
          }
        }}
        onPaste={onPaste}
        required={isRequired}
        rows={rows}
        type={type}
        value={value}
        variant="outlined"
      />
    </NoSsr>
  );
}

export default OutlinedTextField;
